import { Dispatch, SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import { selectableImageType } from "../../Pages/Dashboard/shared/News";
import { NEWS_INPUT_LENGTH } from "../types";

interface UseNewsFormInputsProps {
  setSelectedImage: Dispatch<SetStateAction<selectableImageType | null>>;
  titleValue?: string;
  descriptionValue?: string;
  setIsInteracted?: Dispatch<SetStateAction<boolean>>;
  richTextValue?: string;
  setRichTextValue?: (value: SetStateAction<string>) => void;
  setIsReleaseNotes?: Dispatch<SetStateAction<boolean>>;
}

const useNewsFormInputs = ({
  setSelectedImage,
  titleValue,
  descriptionValue,
  setIsInteracted,
  setIsReleaseNotes
}: UseNewsFormInputsProps) => {
  const { t } = useTranslation();
  const [titleInputError, setTitleInputError] = useState<string | null>(null);
  const [descriptionInputError, setDescriptionInputError] = useState<
    string | null
  >(null);
  const [richTextInputError, setRichTextInputError] = useState<string | null>(
    null
  );
  const [title, setTitle] = useState<string | null>(titleValue || "");
  const [description, setDescription] = useState<string | null>(
    descriptionValue || ""
  );
  const [richTextValue, setRichTextValue] = useState<string>(
    descriptionValue || ""
  );
  const [isRichTextFocused, setIsRichTextFocused] = useState<boolean>(false);

  const validateTitleLength = (e: any) => {
    const inputValue = e.target.value.trim();
    if (inputValue.length > NEWS_INPUT_LENGTH.MaxTitle) {
      setTitleInputError(
        t("Dialog##title not valid", {
          maxNumber: NEWS_INPUT_LENGTH.MaxTitle
        })
      );
    }

    if (inputValue.length === 0) {
      setTitleInputError(t("Dialog##title should not be empty"));
    }
  };

  const validateDescriptionLength = (e: any) => {
    const inputValue = e.target.value.trim();
    if (inputValue.length > NEWS_INPUT_LENGTH.MaxDescription) {
      setDescriptionInputError(
        t("Dialog##description not valid", {
          maxNumber: NEWS_INPUT_LENGTH.MaxDescription
        })
      );
    }

    if (inputValue.length === 0) {
      setDescriptionInputError(t("Dialog##description should not be empty"));
    }
  };

  const validateRichTextLength = (value: any, fromOnBlur?: boolean) => {
    fromOnBlur && setIsRichTextFocused(false);
    const inputValue = value.replace(`<p><br></p>`, "").trim();
    if (inputValue.length > NEWS_INPUT_LENGTH.MaxDescription) {
      setRichTextInputError(
        t("Dialog##description not valid", {
          maxNumber: NEWS_INPUT_LENGTH.MaxDescription
        })
      );
    }

    if (inputValue.length === 0) {
      setRichTextInputError(t("Dialog##description should not be empty"));
    }
  };

  const handleChangeTitleValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsInteracted?.(true);
    setTitle(e.target.value);
    setTitleInputError(null);
    validateTitleLength(e);
  };

  const handleChangeRichTextValue = (value: string) => {
    if (setRichTextValue) {
      const inputValue = value;
      setIsInteracted?.(true);
      setRichTextValue(inputValue);
      setRichTextInputError(null);
      validateRichTextLength(inputValue);
    }
  };

  const resetAllValues = () => {
    setTitle(null);
    setTitleInputError(null);
    setDescription(null);
    setDescriptionInputError(null);
    setRichTextInputError(null);
    setSelectedImage(null);
    setRichTextValue?.("");
    setIsRichTextFocused(false);
    setIsReleaseNotes?.(false);
  };

  const isTitleValid = title && title.length <= NEWS_INPUT_LENGTH.MaxTitle;
  const isDescriptionValid =
    description && description.length < NEWS_INPUT_LENGTH.MaxDescription;

  const isRichTextValid =
    Boolean(richTextValue) &&
    richTextValue.length > 0 &&
    richTextValue.length < NEWS_INPUT_LENGTH.MaxDescription;

  const handleFocus = () => {
    setIsRichTextFocused(true);
  };

  return {
    validateTitleLength,
    titleInputError,
    handleChangeTitleValue,
    title,
    resetAllValues,
    isTitleValid,
    validateDescriptionLength,
    isDescriptionValid,
    descriptionInputError,
    description,
    setTitle,
    setDescription,
    validateRichTextLength,
    isRichTextValid,
    handleChangeRichTextValue,
    richTextInputError,
    richTextValue,
    setIsRichTextFocused,
    isRichTextFocused,
    handleFocus,
    setRichTextValue,
    setTitleInputError
  };
};

export default useNewsFormInputs;
